import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BadgeDollarSign, HandCoins, CheckCircle2, XCircle, Repeat } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CurrencyInput } from './CurrencyInput';
import { vehicleLeadsAPI } from '../../lib/api';
import { fmtCurrency } from '../../lib/financeMath';

const money = (a) => (a == null || a === '' ? '' : fmtCurrency(a));

/**
 * Dealer-side Accept / Refuse / Counter card for an offer or a deposit.
 * Reads the SAME vehicle_sales_requests record the sales desk already lists —
 * shared by the Sales Desk and the manager Command Center.
 */
export const OfferDepositCard = ({ req, onDecided, onOpenThread, tone = 'auto' }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(null);   // null = counter form closed

    const isDeposit = req.request_type === 'vehicle_deposit';
    const kind = isDeposit ? t('offerDeposit.badgeDeposit', 'Deposit') : t('offerDeposit.badgeOffer', 'Offer');
    const amount = isDeposit ? req.deposit_amount : req.offer_amount;
    const md = req.manager_decision;
    const bd = req.buyer_decision;
    const actionable = !md || bd === 'declined';

    const statusText = !md ? t('offerDeposit.statusPending', 'Awaiting your decision')
        : md === 'accepted' ? t('offerDeposit.statusAccepted', 'Accepted')
            : md === 'refused' ? t('offerDeposit.statusRefused', 'Refused')
                : bd === 'accepted' ? t('offerDeposit.statusBuyerAccepted', 'Buyer accepted your counter')
                    : bd === 'declined' ? t('offerDeposit.statusBuyerDeclined', 'Buyer declined your counter')
                        : t('offerDeposit.statusCountered', 'Countered — awaiting the buyer');

    const act = async (decision, extra = {}) => {
        setBusy(true);
        try {
            await vehicleLeadsAPI.decision(req.id, { decision, ...extra });
            toast.success(t('offerDeposit.decisionSaved', 'Sent to the buyer'));
            setForm(null);
            onDecided && onDecided();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('offerDeposit.decisionFailed', 'Could not send that'));
        } finally { setBusy(false); }
    };

    return (
        <div className={`rounded-xl border border-emerald-600/40 bg-emerald-500/5 p-3 space-y-2.5 ${tone === 'dark' ? 'text-slate-100' : 'text-slate-900 dark:text-slate-100'}`}
            data-testid={`offer-card-${req.id.slice(0, 6)}`}>
            <div className="flex items-start gap-2.5">
                <span className="mt-0.5 shrink-0 text-emerald-500">
                    {isDeposit ? <HandCoins className="w-4 h-4" /> : <BadgeDollarSign className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0 text-sm">
                    <p className="font-semibold" data-testid="offer-card-headline">
                        <span className="uppercase text-[10px] tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 mr-1.5">{kind}</span>
                        {money(amount)} · {req.year} {req.make} {req.model} #{req.stock_number}
                    </p>
                    <p className="text-xs opacity-70 mt-0.5">
                        {req.customer?.name}
                        {req.customer?.email ? ` · ${req.customer.email}` : ''}
                        {req.customer?.phone ? ` · ${req.customer.phone}` : ''}
                    </p>
                    {isDeposit && req.requested_hold_until && (
                        <p className="text-xs mt-0.5" data-testid="offer-card-hold">{t('offerDeposit.requestedHold', 'Requested hold until')}: {req.requested_hold_until}</p>
                    )}
                    {req.buyer_note && <p className="text-xs mt-0.5 italic">{t('offerDeposit.buyerNote', 'Buyer note')}: {req.buyer_note}</p>}
                    {(req.counter_amount || req.counter_hold_until) && (
                        <p className="text-xs mt-1 text-amber-500" data-testid="offer-card-counter">
                            {t('offerDeposit.counterAmount', 'Counter')}: {money(req.counter_amount)}
                            {req.counter_hold_until ? ` · ${t('offerDeposit.counterHold', 'Hold until')} ${req.counter_hold_until}` : ''}
                        </p>
                    )}
                    <p className="text-xs mt-1 font-medium" data-testid="offer-card-status">{statusText}</p>
                </div>
                {onOpenThread && (
                    <Button size="sm" variant="ghost" className="shrink-0 text-xs" onClick={() => onOpenThread(req)} data-testid="offer-card-thread">
                        {t('offerDeposit.viewThread', 'Thread')}
                    </Button>
                )}
            </div>

            {actionable && !form && (
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} onClick={() => act('accept')} data-testid="offer-accept">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{t('offerDeposit.deskAccept', 'Accept')}
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => act('refuse')} data-testid="offer-refuse">
                        <XCircle className="w-3.5 h-3.5 mr-1" />{t('offerDeposit.deskRefuse', 'Refuse')}
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setForm({ amount: '', hold: '', note: '' })} data-testid="offer-counter">
                        <Repeat className="w-3.5 h-3.5 mr-1" />{t('offerDeposit.deskCounter', 'Counter')}
                    </Button>
                </div>
            )}

            {form && (
                <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-2.5" data-testid="offer-counter-form">
                    <p className="text-xs font-semibold">{t('offerDeposit.counterFormTitle', 'Counter this {{kind}}', { kind })}</p>
                    <CurrencyInput value={form.amount} onChange={(v) => setForm((p) => ({ ...p, amount: v }))}
                        placeholder={t('offerDeposit.counterFormAmount', 'Counter amount')}
                        className="h-9 text-sm" data-testid="offer-counter-amount" />
                    {isDeposit && (
                        <Input type="date" value={form.hold} onChange={(e) => setForm((p) => ({ ...p, hold: e.target.value }))}
                            className="h-9 text-sm" data-testid="offer-counter-hold" />
                    )}
                    <Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                        placeholder={t('offerDeposit.counterFormNote', 'Note to the buyer (optional)')}
                        className="h-9 text-sm" data-testid="offer-counter-note" />
                    <div className="flex gap-2">
                        <Button size="sm" disabled={busy} data-testid="offer-counter-send"
                            onClick={() => {
                                const amt = parseFloat(String(form.amount).replace(/[^0-9.]/g, ''));
                                if (!amt && !form.hold) { toast.error(t('offerDeposit.errCounter', 'Enter a counter amount or a new hold date')); return; }
                                act('counter', { counter_amount: amt || null, counter_hold_until: form.hold || null, note: form.note || null });
                            }}>
                            {t('offerDeposit.counterFormSend', 'Send counter')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setForm(null)} data-testid="offer-counter-cancel">
                            {t('common.cancel', 'Cancel')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export const isOfferRequest = (r) => r?.request_type === 'vehicle_offer' || r?.request_type === 'vehicle_deposit';

export default OfferDepositCard;
